
import { useCallback } from "react";
import { SlackIntegration } from "../types";
import { logger } from "@/lib/logger";

type UpdateSettingsFunction = (settings: Partial<SlackIntegration>) => Promise<void>;

/**
 * Hook for Slack callback handlers
 */
export const useSlackCallbacks = (
  isConnected: boolean,
  settingsHandlers: {
    handleTagChange: (value: string, updateSettings?: (settings: Partial<SlackIntegration>) => Promise<void>) => void;
    handleFileProcessingChange: (checked: boolean, updateSettings?: (settings: Partial<SlackIntegration>) => Promise<void>) => void;
    handleAutoResponsesChange: (checked: boolean, updateSettings?: (settings: Partial<SlackIntegration>) => Promise<void>) => void;
    handleRoleDetectionChange: (checked: boolean, updateSettings?: (settings: Partial<SlackIntegration>) => Promise<void>) => void;
  },
  updateSettingsCallback: (settings: Partial<SlackIntegration>, saveSettings: (settings: Partial<SlackIntegration>) => Promise<void>) => Promise<void>,
  saveSettings: (settings: Partial<SlackIntegration>) => Promise<void>
) => {
  const updateSettingsFactory = useCallback(
    (callback: UpdateSettingsFunction) => {
      return isConnected ? 
        async (settings: Partial<SlackIntegration>) => {
          await updateSettingsCallback(settings, callback);
        } : 
        undefined;
    },
    [isConnected, updateSettingsCallback]
  );

  const handleTagChange = useCallback(
    (value: string) => {
      settingsHandlers.handleTagChange(value, updateSettingsFactory(saveSettings));
    },
    [settingsHandlers, updateSettingsFactory, saveSettings]
  );
  
  const handleFileProcessingChange = useCallback(
    (checked: boolean) => {
      settingsHandlers.handleFileProcessingChange(checked, updateSettingsFactory(saveSettings));
    },
    [settingsHandlers, updateSettingsFactory, saveSettings]
  );
  
  const handleAutoResponsesChange = useCallback(
    (checked: boolean) => {
      settingsHandlers.handleAutoResponsesChange(checked, updateSettingsFactory(saveSettings));
    },
    [settingsHandlers, updateSettingsFactory, saveSettings]
  );

  const handleRoleDetectionChange = useCallback(
    (checked: boolean) => {
      settingsHandlers.handleRoleDetectionChange(checked, updateSettingsFactory(saveSettings));
    },
    [settingsHandlers, updateSettingsFactory, saveSettings]
  );

  return {
    handleTagChange,
    handleFileProcessingChange,
    handleAutoResponsesChange,
    handleRoleDetectionChange
  };
};
